import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { MdDarkMode, MdLightMode } from "react-icons/md";
import { IoMdArrowRoundForward } from "react-icons/io";
// import { FaLinkedin } from 'react-icons/fa';
// // import { useHistory } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { HelmetProvider, Helmet } from 'react-helmet-async';
import Summary from './Summary';
import Schedule from './Schedule';
import ScheduleDTDC from './ScheduleDTDC';
import ShareAchievementButton from './ShareAchievementButton';
import './App.css';

// Custom Modal Component
const Modal = ({ show, onClose, onConfirm, message }) => {
  if (!show) return null;
  return (
    <div className="modal">
      <div className="modal-content fade-in">
        <p>{message}</p>
        <button onClick={onConfirm} className="modal-button">Yes</button>
        <button onClick={onClose} className="modal-button">No</button>
      </div>
    </div>
  );
};

const getName = (name) => {
  const words = name.split(' ').filter(word => word.length > 0);
  
  let selectedName = words[0];
  
  if (selectedName.length <= 2) {
    const longerName = words.find(word => word.length > 2);
    if (longerName) {
      selectedName = longerName;
    } else {
      selectedName = words[words.length - 1];
      return selectedName;
    }
  }
  
  // Check if selectedName is a variation of Mohamed/Mohammed
  const mohamedVariants = ['MOHAIDEEN', 'MOHAMED', 'MOHAMMADU', 'MOHAMMED'];
  const isModamedVariant = mohamedVariants.some(variant => 
    selectedName.toUpperCase() === variant
  );
  
  if (isModamedVariant) {
    // Try to find another word with length > 2 that's not a Mohamed variant
    const alternativeName = words.find(word => 
      word.length > 2 && 
      !mohamedVariants.some(variant => word.toUpperCase() === variant) &&
      word !== selectedName
    );
    if (alternativeName) {
      selectedName = alternativeName;
    }
    // If no alternative found, keep the Mohamed variant
  }
  
  return selectedName.toLowerCase().charAt(0).toUpperCase() + selectedName.slice(1).toLowerCase();
}

const getGreeting = (name, isFirstLogin) => {
  const date = new Date().toLocaleString('en-GB', { 
    timeZone: 'Asia/Kolkata',
    hour12: false
  });
  const hour = parseInt(date.split(',')[1].split(':')[0]);
  
  const capitalizedName = getName(name);

  if (isFirstLogin) {
    return `${capitalizedName} returns! 😊`
  }

  let greeting = '';
  if (hour >= 5 && hour < 12) {
    greeting = 'Hey there';
  } else if (hour >= 12 && hour < 17) {
    greeting = 'Good afternoon';
  } else if (hour >= 17 && hour < 21) {
    greeting = 'returns!';
    return `${capitalizedName} ${greeting} 😊`;
  } else {
    greeting = 'Hello, night owl';
    return `${greeting} ${capitalizedName} 😊`;
  }
  
  return `${greeting}, ${capitalizedName} 😊`;
}

const ReleaseNote = () => {
  return (
    <div className="release-note">
      <span className="new-badge">NEW</span>
      📱 SkillRack Tracker is now on Android!
      <br />
      <br />
      <ol
        style={{
          textAlign: "left",
          display: "inline-block",
          margin: "0",
          paddingLeft: "20px",
        }}
      >
        <li style={{ marginBottom: "6px" }}>
          <b>Join the Beta Program: </b>
          <a
            href="https://play.google.com/apps/testing/in.gururaja.skillrack"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline", color: "var(--link-color)" }}
          >
            Click here to join
          </a>
        </li>
        <li style={{ marginBottom: "6px" }}>
          <b>Install the App: </b>
          <a
            href="https://play.google.com/store/apps/details?id=in.gururaja.skillrack"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline", color: "var(--link-color)" }}
          >
            Download on Play Store
          </a>
        </li>
        <li>
          <b>Provide Feedback: </b>
          <a
            href="https://forms.gle/bUQtp3ws9w5NBuma9"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline", color: "var(--link-color)" }}
          >
            Share your thoughts
          </a>
        </li>
      </ol>
    </div>
  );
};

// Sanitize URL to handle duplicated URLs
const sanitizeUrl = (url) => {
  // If URL appears to be duplicated, fix it
  if (url.indexOf('http', 10) > 0) {  // If 'http' appears again after the start
    const httpIndices = [];
    let index = -1;
    while ((index = url.indexOf('http', index + 1)) >= 0) {
      httpIndices.push(index);
    }
    
    if (httpIndices.length > 1) {
      // Take only the first URL
      url = url.substring(0, httpIndices[1]);
    }
  }
  
  // Remove any text after URL-encoded spaces (%20)
  if (url.includes('%20')) {
    url = url.split('%20')[0];
  }
  
  return url;
}

// Allow only SkillRack Profile or Resume URLs
const isProfileOrResumeUrl = (url) => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname !== 'www.skillrack.com') return false;
    const path = parsed.pathname || '';
    // Examples:
    // - http://www.skillrack.com/profile/466980/<key>
    // - https://www.skillrack.com/faces/resume.xhtml?id=...&key=...
    if (path.startsWith('/profile/')) return true;
    if (path === '/faces/resume.xhtml') return true;
    return false;
  } catch (_) {
    return false;
  }
}

const App = () => {

  // const [isStandalone, setIsStandalone] = useState(false);
  // const [isMobile, setIsMobile] = useState(false);

  const [state, setState] = useState({
    url: '',
    points: 0,
    percentage: 0,
    solvedPercentage: 0,
    error: '',
    isErrorMessage: true,
    isValidUrl: false,
    lastFetched: null,
    name: '',
    solved: 0,
    codeTutor: 0,
    codeTrack: 0,
    codeTest: 0,
    dt: 0,
    dc: 0,
    requiredPoints: 0,
    requiredSolved: 0,
    deadline: null,
    college: null,
    year: 0,
    dept: null,
    showSchedule: false,
    showScheduleDTDC: false,
    loading: false,
    showLogoutModal: false,
    isFirstLogin: false
  });

  const achievementCardRef = useRef(null);
  const screenshotRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [theme, setTheme] = useState(() => {
    const savedTheme = Cookies.get('theme');
    if (savedTheme) return savedTheme;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const [isVisible, setIsVisible] = useState(true);
  const [trackingMode, setTrackingMode] = useState(() => {
    const savedMode = Cookies.get('trackingMode');
    return savedMode || 'points'; // Default to points mode
  });
  const handleStateChange = useCallback((newState) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  }, []);

  const fetchData = useCallback((data) => {
    handleStateChange({
      name: data.name,
      points: data.points,
      percentage: data.percentage,
      solvedPercentage: data.solvedPercentage,
      lastFetched: data.lastFetched,
      solved: data.solved,
      codeTutor: data.codeTutor,
      codeTrack: data.codeTrack,
      codeTest: data.codeTest,
      dt: data.dt,
      dc: data.dc,
      requiredPoints: data.requiredPoints,
      requiredSolved: data.requiredSolved,
      deadline: data.deadline,
      college: data.college,
      year: data.year,
      dept: data.dept
    });
  }, [handleStateChange]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    // Save theme to cookie
    Cookies.set('theme', theme, { 
      expires: 365,
      sameSite: 'Lax',
      secure: true
    }); // Expires in 1 year
  }, [theme]);

  useEffect(() => {
    // // Check if the app is running as an installed PWA
    // const checkStandalone = () => {
    //   if (window.matchMedia('(display-mode: standalone)').matches) {
    //     setIsStandalone(true);
    //   } else if (window.navigator.standalone) {  // For iOS devices
    //     setIsStandalone(true);
    //   } else {
    //     setIsStandalone(false);
    //   }
    // };

    // // Check if the user is on a mobile device
    // const checkMobile = () => {
    //   setIsMobile(window.innerWidth <= 768); // Adjust the width as necessary
    // };

    // checkStandalone();
    // checkMobile();

    // window.addEventListener('resize', checkMobile);

    const fetchInitialData = async () => {
      const lastUrl = Cookies.get('lastUrl');

      if (lastUrl) {
        try {
          const cleanedLastUrl = sanitizeUrl(lastUrl);
          if (!isProfileOrResumeUrl(cleanedLastUrl)) {
            handleStateChange({
              error: 'Invalid URL! Please watch the above Tutorial video!! If issue persists for longer time, contact mail@gururaja.in',
              isErrorMessage: true,
              loading: false
            });
            Cookies.remove('lastUrl');
            throw new Error('Blocked non profile/resume URL in refresh');
          }
          const { data } = await axios.get(`/api/points/refresh?url=${encodeURIComponent(cleanedLastUrl)}`);
          if (data && data.requireRelogin) {
            // Version mismatch - force logout
            // Reset state directly instead of using initialState object
            handleStateChange({
              url: '',
              points: 0,
              percentage: 0,
              solvedPercentage: 0,
              error: 'Application has been updated! Please login again to continue.',
              isErrorMessage: false,
              isValidUrl: false,
              lastFetched: null,
              name: '',
              solved: 0,
              codeTutor: 0,
              codeTrack: 0,
              codeTest: 0,
              dt: 0,
              dc: 0,
              requiredPoints: 0,
              requiredSolved: 0,
              deadline: null,
              college: null,
              year: 0,
              dept: null,
              showSchedule: false,
              showScheduleDTDC: false,
              loading: false
            });
            Cookies.remove('lastUrl');
            Cookies.remove('finishDate');
            Cookies.remove('manualTarget');
            Cookies.remove('targetPoints');
            Cookies.remove('targetSolved');
          } else if (data && data.name !== '') {
            fetchData(data);
            handleStateChange({ isValidUrl: true, url: lastUrl });
          }
        } catch (error) {
                      if (error.response && error.response.status === 429) {
              handleStateChange({ error: 'Too many requests! Please try again after 60 seconds!!', isErrorMessage: true, loading: false });
            }
          Cookies.remove('lastUrl');
        }
      }
      setIsInitialized(true);
      setIsLoading(false);
    };

    fetchInitialData();
  }, [fetchData, handleStateChange]);

  // Theme management
  const toggleTheme = () => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light';
      return newTheme;
    });
  };

  // Toggle tracking mode
  const toggleTrackingMode = () => {
    setTrackingMode(prevMode => {
      const newMode = prevMode === 'points' ? 'solved' : 'points';
      return newMode;
    });
  };

  // Save tracking mode to cookies
  useEffect(() => {
    Cookies.set('trackingMode', trackingMode, {
      expires: 365,
      sameSite: 'Lax',
      secure: true
    });
  }, [trackingMode]);

  // Scroll handling
  useEffect(() => {
    let prevScrollPos = window.scrollY;
    
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      setIsVisible(prevScrollPos > currentScrollPos || currentScrollPos < 50);
      prevScrollPos = currentScrollPos;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isValidSkillRackUrl = (url) => {
    const regex = /^https?:\/\/www\.skillrack\.com/;
    return regex.test(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    handleStateChange({ error: '', isErrorMessage: true });

    // Clean the URL - remove trailing spaces and sanitize duplicated URLs
    const cleanedUrl = sanitizeUrl(state.url.trim());

    if (!isValidSkillRackUrl(cleanedUrl)) {
      handleStateChange({ error: 'Invalid URL! Please watch the above Tutorial video!!', isErrorMessage: true });
      return;
    }

    if (!isProfileOrResumeUrl(cleanedUrl)) {
      handleStateChange({
        error: 'Invalid URL! Please watch the above Tutorial video!! If issue persists for longer time, contact mail@gururaja.in',
        isErrorMessage: true
      });
      return;
    }

    handleStateChange({ loading: true });
    try {
      const { data } = await axios.post('/api/points', { url: cleanedUrl.split("%")[0] });
      if (data && data.name !== '') {
        fetchData(data);
        handleStateChange({ isValidUrl: true, loading: false, isFirstLogin: true });
        Cookies.set('lastUrl', data.url, {
          expires: 30, // Set to expire in 1 month
          sameSite: 'Lax',
          secure: true
        });
      } else {
        handleStateChange({ error: 'Invalid URL! Please watch the above Tutorial video!! If issue persists for longer time, contact mail@gururaja.in', isErrorMessage: true, loading: false });
      }
    } catch (error) {
      if (error.response && error.response.status === 429) {
        handleStateChange({ error: 'Too many requests! Please try again after 60 seconds!!', isErrorMessage: true, loading: false });
      } else {
        handleStateChange({ error: 'Invalid URL! Please watch the above Tutorial video!! If issue persists for longer time, contact mail@gururaja.in', isErrorMessage: true, loading: false });
      }
    }
    handleStateChange({ loading: false });
  };

  const handleLogout = () => {
    handleStateChange({ showLogoutModal: true });
  };

  const confirmLogout = () => {
    // Reset state directly instead of using initialState object
    handleStateChange({
      url: '',
      points: 0,
      percentage: 0,
      solvedPercentage: 0,
      error: '',
      isErrorMessage: true,
      isValidUrl: false,
      lastFetched: null,
      name: '',
      solved: 0,
      codeTutor: 0,
      codeTrack: 0,
      codeTest: 0,
      dt: 0,
      dc: 0,
      requiredPoints: 0,
      requiredSolved: 0,
      deadline: null,
      college: null,
      year: 0,
      dept: null,
      showSchedule: false,
      showScheduleDTDC: false,
      loading: false,
      showLogoutModal: false,
      isFirstLogin: false
    });
    Cookies.remove('lastUrl');
    Cookies.remove('finishDate');
    Cookies.remove('manualTarget');
    Cookies.remove('targetPoints');
    Cookies.remove('targetSolved');
  };

  const handleGenerateSchedule = () => {
    handleStateChange({ showSchedule: true });
  };

  const handleGenerateScheduleDTDC = () => {
    handleStateChange({ showScheduleDTDC: true });
  };

  // Don't render anything until initialization is complete or while loading
  if (!isInitialized || isLoading || state.loading) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '50px',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--background-color)'
      }}>
        <h1 style={{
          animation: 'pulse 2s infinite'
        }}>
          Loading...
        </h1>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Helmet>
          <title>SkillRack Tracker</title>
          <meta property="og:title" content="SkillRack Tracker" />
          <meta name="description" content="Track and calculate your SkillRack points effortlessly using this powerful tool." />
          <link rel="canonical" href="https://skillrack.gururaja.in" />
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "WebSite",
                "name": "SkillRack Tracker",
                "alternateName": "SkillTrack",
                "url": "https://skillrack.gururaja.in/",
                "description": "Track and calculate your SkillRack points effortlessly using this powerful tool.",
                "author": {
                  "@type": "Person",
                  "name": "Guru"
                }
              }
            `}
          </script>
        </Helmet>
        <h1>SkillRack Tracker</h1>
        <Analytics/>
        <SpeedInsights/>
        <button 
          className={`theme-toggle ${isVisible ? 'visible' : 'hidden'}`}
          onClick={toggleTheme}
          aria-label="Toggle theme"
        >
          {theme === 'light' ? <MdDarkMode color='orange' size={18} /> : <MdLightMode color='orange' size={18} />}
        </button>
        {!state.isValidUrl && (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            <p style={{maxWidth: 600}}>Login to <a href="https://www.skillrack.com/faces/candidate/manageprofile.xhtml" target="_blank" rel="noopener noreferrer"><b>SkillRack</b></a> <IoMdArrowRoundForward size={12}/> Profile <IoMdArrowRoundForward size={12}/> Enter Password <IoMdArrowRoundForward size={12}/> Click "View" <IoMdArrowRoundForward size={12}/> Copy the URL<br /><br />Tutorial: <a href="https://youtu.be/BsrCYe9Ytuw" target="_blank" rel="noopener noreferrer"><b>Watch on YouTube</b></a></p>
            <input
              type="password"
              value={state.url}
              onChange={(e) => {
                const inputUrl = e.target.value.trim();
                const sanitizedUrl = sanitizeUrl(inputUrl);
                handleStateChange({ url: sanitizedUrl });
              }}
              placeholder="Paste Profile URL"
              name="profile_url"
              style={{ width: '100%', maxWidth: '300px', padding: '10px', boxSizing: 'border-box' }}
            />
            <button type="submit" className="submit-button">Submit</button>
            {state.error && (
              <p style={{ 
                color: state.isErrorMessage ? 'var(--error-color)' : 'var(--update-color)',
              }}>
                {state.error}
              </p>
            )}
          </form>
        )}
        {state.isValidUrl && (
          <>
            { <ReleaseNote /> }
            <div ref={achievementCardRef}>
              <p>Updated on {state.lastFetched}</p>
              <br style={{ marginBottom: '15px' }} />
              <h2 className='fix-width'>
                {state.isFirstLogin ? getGreeting(state.name, true) : getGreeting(state.name, false)}
              </h2>
              
              {/* Toggle between points and solved mode */}
              {state.solved > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '20px 0' }}>
                  <span style={{ marginRight: '10px', fontWeight: trackingMode === 'points' ? 'bold' : 'normal' }}>Points</span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={trackingMode === 'solved'}
                      onChange={toggleTrackingMode}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span style={{ marginLeft: '10px', fontWeight: trackingMode === 'solved' ? 'bold' : 'normal' }}>Programs</span>
                </div>
              )}
              
              <div style={{ width: '200px', margin: '50px auto 35px auto' }}>
                <CircularProgressbar
                  value={trackingMode === 'points' ? state.percentage : state.solvedPercentage}
                  text={
                    trackingMode === 'points' 
                      ? ((state.points <= state.requiredPoints) && (state.points !== 0) 
                          ? `${state.points}/${state.requiredPoints}` 
                          : `${state.points}`)
                      : ((state.solved <= state.requiredSolved) && (state.solved !== 0) 
                          ? `${state.solved}/${state.requiredSolved}` 
                          : `${state.solved}`)
                  }
                  styles={buildStyles({
                    // Use CSS variables for theme-aware colors
                    textColor: 'var(--progress-text)',
                    pathColor: 'var(--progress-circle)',
                    trailColor: 'var(--input-border)',
                    textSize: '16px',
                    // Add transition for smooth theme switching
                    transition: 'stroke-dashoffset 0.5s ease 0s',
                  })}
                />
              </div>

              {((trackingMode === 'points' && state.points >= state.requiredPoints && state.requiredPoints !== 0) ||
                (trackingMode === 'solved' && state.solved >= state.requiredSolved && state.requiredSolved !== 0)) && (
                <>
                  <h3 className='fix-width'>
                    Congratulations 🎉 {getName(state.name)} on completing {
                      trackingMode === 'points' ? `${state.requiredPoints} points` : `${state.requiredSolved} programs`
                    }!
                  </h3>
                  <br />
                </>
              )}

              {/* Share Achievement Button */}
              <ShareAchievementButton
                name={state.name}
                points={state.points}
                solved={state.solved}
                requiredPoints={state.requiredPoints}
                requiredSolved={state.requiredSolved}
                percentage={state.percentage}
                solvedPercentage={state.solvedPercentage}
                trackingMode={trackingMode}
                college={state.college}
                year={state.year}
                dept={state.dept}
                cardRef={achievementCardRef}
                screenshotRef={screenshotRef}
                lastFetched={state.lastFetched}
                isFirstLogin={state.isFirstLogin}
              />
              <br /> <br />

              <Summary
                solved={state.solved}
                codeTutor={state.codeTutor}
                codeTrack={state.codeTrack}
                codeTest={state.codeTest}
                dt={state.dt}
                dc={state.dc}
                totalPoints={state.points}
                percentage={state.percentage}
                solvedPercentage={state.solvedPercentage}
                trackingMode={trackingMode}
                requiredSolved={state.requiredSolved}
              />
            </div>
            
            {/* Hidden div for LinkedIn-optimized screenshot capture only */}
            <div ref={screenshotRef} style={{ 
              position: 'absolute',
              left: '-9999px',
              top: '-9999px',
              width: '600px',
              padding: '40px',
              backgroundColor: '#1a1a1a',
              borderRadius: '16px',
              textAlign: 'center',
              color: '#ffffff',
              boxSizing: 'border-box'
            }}>
              <h1 style={{ 
                fontSize: '2em', 
                marginBottom: '15px',
                fontWeight: 'bold',
                color: '#00d26a'
              }}>SkillRack Tracker</h1>
              
              <p style={{ fontSize: '0.95em', marginBottom: '20px', color: '#cccccc' }}>Updated on {state.lastFetched}</p>
              
              <h2 style={{ fontSize: '1.5em', marginBottom: '25px', fontWeight: 'bold' }}>
                {state.isFirstLogin ? getGreeting(state.name, true) : getGreeting(state.name, false)}
              </h2>
              
              {/* Toggle display */}
              {state.solved > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '25px' }}>
                  <span style={{ marginRight: '10px', fontWeight: trackingMode === 'points' ? 'bold' : 'normal', fontSize: '1em' }}>Points</span>
                  <div style={{ 
                    width: '50px', 
                    height: '26px', 
                    backgroundColor: trackingMode === 'solved' ? '#00d26a' : '#333333',
                    borderRadius: '13px',
                    position: 'relative',
                    margin: '0 10px'
                  }}>
                    <div style={{
                      width: '22px',
                      height: '22px',
                      backgroundColor: '#ffffff',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '2px',
                      left: trackingMode === 'solved' ? '26px' : '2px',
                      transition: 'left 0.3s ease'
                    }}></div>
                  </div>
                  <span style={{ marginLeft: '10px', fontWeight: trackingMode === 'solved' ? 'bold' : 'normal', fontSize: '1em' }}>Programs</span>
                </div>
              )}
              
              {/* Progress Circle */}
              <div style={{ width: '220px', margin: '25px auto' }}>
                <CircularProgressbar
                  value={trackingMode === 'points' ? state.percentage : state.solvedPercentage}
                  text={
                    trackingMode === 'points' 
                      ? ((state.points <= state.requiredPoints) && (state.points !== 0) 
                          ? `${state.points}/${state.requiredPoints}` 
                          : `${state.points}`)
                      : ((state.solved <= state.requiredSolved) && (state.solved !== 0) 
                          ? `${state.solved}/${state.requiredSolved}` 
                          : `${state.solved}`)
                  }
                  styles={buildStyles({
                    textColor: '#ffffff',
                    pathColor: '#00d26a',
                    trailColor: '#333333',
                    textSize: '16px'
                  })}
                />
              </div>

              {/* Congratulations message */}
              {((trackingMode === 'points' && state.points >= state.requiredPoints && state.requiredPoints !== 0) ||
                (trackingMode === 'solved' && state.solved >= state.requiredSolved && state.requiredSolved !== 0)) && (
                <h3 style={{ fontSize: '1.3em', marginTop: '20px', fontWeight: 'bold' }}>
                  Congratulations 🎉 {getName(state.name)} on completing {
                    trackingMode === 'points' ? `${state.requiredPoints} points` : `${state.requiredSolved} programs`
                  }!
                </h3>
              )}
            </div>
            
            <br /><br />
            
            {((state.codeTutor + state.codeTrack + state.codeTest) >= 600) && (
              <>
                <button onClick={handleGenerateSchedule} className="generate-schedule-button">✨ Plan with AI ✨</button><br /><br /><br /><br />
                {state.showSchedule && (
                  <div className="fade-in">
                    <Schedule
                      initialValues={{
                        codeTrack: state.codeTrack,
                        dt: state.dt,
                        dc: state.dc,
                        points: state.points,
                        requiredPoints: state.requiredPoints,
                        deadline: state.deadline,
                        codeTest: state.codeTest,
                        solved: state.solved,
                        requiredSolved: state.requiredSolved,
                        codeTutor: state.codeTutor
                      }}
                      trackingMode={trackingMode}
                    />
                    <br />
                  </div>
                )}
              </>
            )}
            {(state.codeTutor + state.codeTrack + state.codeTest) < 600 && (
              <>
                <button onClick={handleGenerateScheduleDTDC} className="generate-schedule-button">✨ Plan with AI ✨</button><br /><br /><br /><br />
                {state.showScheduleDTDC && (
                  <div className="fade-in">
                    <ScheduleDTDC
                      initialValues={{
                        codeTrack: state.codeTrack,
                        problems: state.codeTrack + state.codeTutor + state.codeTest,
                      }}
                    />
                    <br />
                  </div>
                )}
              </>
            )}
          </>
        )}
        <footer>
          {/* {!isStandalone && isMobile && (
            <a href="https://play.google.com/store/apps/details?id=in.gururaja.in.skillrack" target="_blank" rel="noopener noreferrer">
              <img src=".assets/badge_googleplay.png" alt="Get it on Google Play" height="80" />
            </a>
          )}
          <br /><br /> */}
          {state.isValidUrl && (
            <>
              <details>
                <summary>&nbsp;Support this Service!</summary>
                <br />
                <p style={{ maxWidth: "350px", textAlign: "center", margin: "0 auto" }}>
                    If you find this platform helpful, a small donation of ₹5-10 can help us cover server costs and improve the service. 
                    Every contribution makes a difference, and we truly appreciate your support!
                </p>
                <p>
                  <strong>Donate here:&nbsp;
                    <a 
                        href="https://gururaja.in/donate" 
                        target="_blank" 
                        rel="noopener noreferrer"
                    >
                        Click to Donate
                    </a>
                  </strong>
                </p>
              </details>
              <br /><br />
              <div className='para-container'>
                <ul>
                  <li><b>College</b>: {state.college.includes(",") ? state.college.split(",")[0] : state.college}</li>
                  <li><b>Year</b>: {state.year}</li>
                  <li><b>Required Points</b>: {state.requiredPoints !== 0 ? state.requiredPoints : <i>Unknown</i>}</li>
                  <li><b>Programs Count</b>: {state.requiredSolved !== 0 ? state.requiredSolved : <i>Unknown</i>}</li>
                  <li><b>Deadline</b>: {state.deadline !== null ? new Date(state.deadline).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : <i>Unknown</i>}</li>
                </ul>
                <div style={{paddingBottom: '16px'}}>
                  <details>
                    <summary style={{textAlign: 'center'}}>&nbsp;Report</summary>
                      <br/>
                      Email <a href="mailto:mail@gururaja.in">mail@gururaja.in</a> with your correct college-assigned details, or just drop a message in the <a href="https://t.me/+zyOTdSvO804yYjE1" target="_blank" rel="noopener noreferrer">group</a>
                  </details>
                </div>
              </div>
              <br /><br />
            </>
          )}
          {/* <ReleaseNote /> */}
          {state.isValidUrl && (
            <>
              <button onClick={handleLogout} className="logout-button">Logout</button>
              
            </>
          )}
          <br /><br /><br /><br />
          <div style={{ 
            fontSize: '14px', 
            color: 'var(--text-muted)', 
            lineHeight: '1.6',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>Made with ❤️ by <a href="https://gururaja.in" target="_blank" rel="noopener noreferrer" style={{color: 'var(--link-color)', fontWeight: 'bold'}}>someone</a></span>
            <span>•</span>
            {!state.isValidUrl && (
              <a href="https://gururaja.in/legal" target="_blank" rel="noopener noreferrer" style={{color: 'var(--link-color)', textDecoration: 'none'}}>Privacy & Terms</a>
            )}
            {state.isValidUrl && (
              <a href="https://t.me/+zyOTdSvO804yYjE1" target="_blank" rel="noopener noreferrer" style={{color: 'var(--link-color)', textDecoration: 'none'}}>Be part of the team</a>
            )}
          </div>

          {/* <br /><br /><br />An <a href="https://github.com/Guru-25/SkillRack-Tracker" target="_blank" rel="noopener noreferrer"><b>Initiative</b></a> 🚀 from TCE IT */}
        </footer>
        <Modal
          show={state.showLogoutModal}
          onClose={() => handleStateChange({ showLogoutModal: false })}
          onConfirm={confirmLogout}
          message="Are you sure you want to Logout?"
        />
      </div>
    </HelmetProvider>
  );
};

export default App;

